import type { Ref } from 'vue';
import type { PatientDetails, VerifyPatientResult } from '../types';
import { sendVerificationCodeApi, verifyPatientDetailsApi } from '../services/session.service';

type UseVerifyPatientDetailsDeps = {
  patientDetails: Ref<PatientDetails | null>;
  isProxy: Ref<boolean>;
  transactionId?: Ref<string | null>;
  setApiLoading: () => void;
  setApiDone: () => void;
  setIsValidPatient: (value: boolean) => void;
  setIsVerified?: (value: boolean) => void;
  sendVerificationCodeOnSuccess?: boolean;
};

export function useVerifyPatientDetails(deps: UseVerifyPatientDetailsDeps) {
  return async function verifyPatientDetails(
    details: PatientDetails,
  ): Promise<VerifyPatientResult> {
    deps.patientDetails.value = details;
    deps.setApiLoading();
    try {
      const result: VerifyPatientResult = await verifyPatientDetailsApi(
        details,
        deps.isProxy.value,
      );

      if (result.ok) {
        deps.setIsValidPatient(true);
        if (deps.sendVerificationCodeOnSuccess) {
          await sendVerificationCodeApi(details, deps.transactionId.value, false);
        }
        return {
          ok: true as const,
        };
      }

      // Previously fell through and resolved undefined, so the caller had to
      // guard with `result?.ok`.
      return { ok: false as const, errors: result.errors };
    } catch (e) {
      console.error('verifyPatientDetails error', e);
      return { ok: false as const };
    } finally {
      deps.setApiDone();
    }
  };
}
